import { WS_COLOR, WS_CONTROL, WS_FONT, WS_LAYOUT, WS_RADIUS, WS_STATUS, WS_TYPE } from './tokens';

/**
 * Styles that more than one layout or table needs. They live here once so a change (a surface
 * border, the narrow rule, the table header) is made in one place.
 */

export const NARROW_WIDTH = WS_LAYOUT.narrow;

/** Use as an `sx` key: `[NARROW_QUERY]: { ... }`. Answers to the layout's own width. */
export const NARROW_QUERY = `@container (max-width: ${NARROW_WIDTH}px)`;

/** A flat white surface with the one border and card radius. Never nested inside another. */
export const SURFACE_SX = {
  backgroundColor: WS_COLOR.surface,
  border: `1px solid ${WS_COLOR.border}`,
  borderRadius: `${WS_RADIUS.card}px`,
} as const;

/** A region that scrolls on its own inside a flex or grid parent. */
export const SCROLL_REGION_SX = { minHeight: 0, minWidth: 0, overflowY: 'auto' } as const;

/** Screen-reader-only text. NOTE `1px`, never `1`: in `sx` a number from 0 to 1 is a fraction. */
export const VISUALLY_HIDDEN_SX = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  margin: '-1px',
  padding: 0,
  border: 0,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
} as const;

/** When narrow, a layout becomes one column and its body is the single scroll region. */
export const STACK_WHEN_NARROW_SX = {
  [NARROW_QUERY]: {
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
    '& > *': { flex: 'none', overflow: 'visible' },
  },
} as const;

const [bodyFont, bodyLine] = WS_TYPE.table;
const [headFont, headLine, headWeight] = WS_TYPE.tableHeader;

/**
 * THE table style (DESIGN.md "one table foundation"). Used by `DataSurface`, so the registry
 * table, the financial grid and the comparison matrix cannot drift apart.
 *
 * Sticky works on the `th` (not the `tr`), with `border-collapse: separate` and no ancestor
 * between the `th` and the scroll box having a non-visible overflow (CLAUDE.md "A screen header
 * never scrolls"). `stickyFirstColumn` also pins each row's `th[scope=row]` for wide grids.
 */
export function tableSx(rowHeight: number, stickyFirstColumn: boolean) {
  return {
    '& table': {
      width: '100%',
      borderCollapse: 'separate',
      borderSpacing: 0,
      fontSize: bodyFont,
      lineHeight: `${bodyLine}px`,
      color: WS_COLOR.text,
    },
    '& th': {
      position: 'sticky',
      top: 0,
      zIndex: 1,
      backgroundColor: WS_COLOR.surfaceSubtle,
      textAlign: 'left',
      fontSize: headFont,
      lineHeight: `${headLine}px`,
      fontWeight: headWeight,
      padding: '10px 12px',
      borderBottom: `1px solid ${WS_COLOR.border}`,
      whiteSpace: 'nowrap',
    },
    '& th[data-numeric], & td[data-numeric]': {
      textAlign: 'right',
      fontVariantNumeric: 'tabular-nums lining-nums',
    },
    '& td': {
      height: rowHeight,
      padding: '0 12px',
      borderBottom: `1px solid ${WS_COLOR.border}`,
      whiteSpace: 'nowrap',
    },
    '& td[data-mono]': { fontFamily: WS_FONT.mono, fontSize: 12 },
    '& tbody tr:last-of-type td, & tbody tr:last-of-type th': { borderBottom: 0 },
    '& tbody tr[tabindex]': { cursor: 'pointer' },
    '& tbody tr[tabindex]:hover td': { backgroundColor: WS_COLOR.surfaceSubtle },
    '& tbody tr[data-selected] td': {
      backgroundColor: WS_STATUS.info.fill,
      color: WS_STATUS.info.ink,
    },
    '& td[data-emphasis], & tbody th[data-emphasis]': { fontWeight: 700 },
    '& td[data-best]': {
      backgroundColor: WS_STATUS.info.fill,
      color: WS_STATUS.info.ink,
      fontWeight: 700,
    },
    ...(stickyFirstColumn
      ? {
          '& tbody th[scope=row]': {
            position: 'sticky',
            left: 0,
            zIndex: 1,
            height: rowHeight,
            textAlign: 'left',
            fontSize: bodyFont,
            lineHeight: `${bodyLine}px`,
            fontWeight: 500,
            backgroundColor: WS_COLOR.surface,
            borderRight: `1px solid ${WS_COLOR.border}`,
            borderBottom: `1px solid ${WS_COLOR.border}`,
          },
          '& thead th:first-of-type': {
            left: 0,
            zIndex: 3,
            borderRight: `1px solid ${WS_COLOR.border}`,
          },
        }
      : {}),
  } as const;
}

export const TABLE_ROW_HEIGHT = WS_CONTROL.rowHeight;
